import React, { useContext } from 'react';
import { ProductContext } from '../../ProductContext';
import { StoreLocatorAction } from './StoreLocatorAction';
import { StoreLocatorTile } from './StoreLocatorTile';
import { LoadingMoreSpinner } from 'src/scripts/templates/product-listing-page/components/SmallUIComponents';
import { openStoreLocatorSidebar } from './storeLocatorSidebarState';
import { observer } from 'mobx-react-lite';
import { bopisState } from '../Bopis/bopisState';
import { ShippingCountryState } from '../../../../utils/shipping';
import { IStoreAvailabilityData, StoreLocatorType } from './storeLocatorTypes';
import StoreLocatorTitle from './StoreLocatorTitle';
import { findInStoreState } from '../FindInStore/findInStoreState';
import { SiteSettings } from 'src/scripts/utils/environment';
import './style.scss';
import { isEmpty } from 'lodash-es';
import { useCountryCode } from 'src/scripts/templates/_shared/react-hooks';
import { transformPdpDataToBopisAnalyticsVariant } from '../Bopis/bopisUtils';
import { BopisAnalyticsData } from '../Bopis/bopisTypes';

let StoreLocatorPDP = ({
  title,
  userSelectedSize,
  storeLocatorType,
  isLoading = false,
  shouldShowOmniPdpExperience = false,
}: {
  title: string;
  userSelectedSize: string | undefined;
  storeLocatorType: StoreLocatorType;
  isLoading?: boolean;
  shouldShowOmniPdpExperience?: boolean;
}) => {
  const storesData = storeLocatorType === 'fis' ? findInStoreState.storesData?.slice(0, 1) : bopisState.storeSelection;
  const pricingDisclaimer = SiteSettings?.findInStorePricingDisclaimer;
  const countryCode = useCountryCode();
  const { sidebarPdpData } = useContext(ProductContext);
  const product = sidebarPdpData?.product;
  const selectedVariant = sidebarPdpData?.selectedVariant;
  const bopisAnalyticsVariant =
    selectedVariant && product?.title ? transformPdpDataToBopisAnalyticsVariant(selectedVariant, product) : undefined;

  const bopisEventDetailsTemplate = {
    countryCode,
    page: 'PDP' as BopisAnalyticsData['page'],
    isSidebar: false,
    variants: bopisAnalyticsVariant ? [bopisAnalyticsVariant] : [],
  };

  const showError = storeLocatorType === 'fis' ? findInStoreState.storesDataError : bopisState.pdpStoresDataError;
  const noResultsCopy =
    storeLocatorType === 'fis'
      ? `Sorry, there are no ALO stores found near you. Try a different ${
          ShippingCountryState.countryCode.toLowerCase() === 'us' ? 'zip' : 'postal'
        } code.`
      : 'No stores found near you. Try a different zip code or have it shipped.';

  if (showError) {
    // If there's an error, return the error UI
    return (
      <div className="store-locator-pdp">
        {!shouldShowOmniPdpExperience ? <StoreLocatorTitle title={title} storeLocatorType={storeLocatorType} /> : null}
        <StoreLocatorAction storeLocatorType={storeLocatorType} analyticsEventDetails={bopisEventDetailsTemplate} />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingMoreSpinner />;
  }

  const isStoreDataEmpty =
    !storesData || storesData?.length === 0 || (storeLocatorType === 'bopis' && isEmpty(bopisState.storeSelection));

  return (
    <div className="store-locator-pdp">
      {!shouldShowOmniPdpExperience ? <StoreLocatorTitle title={title} storeLocatorType={storeLocatorType} /> : null}
      {ShippingCountryState.countryCode ? (
        !userSelectedSize ? (
          <p className="store-inventory-message">Select a size to check store inventory</p>
        ) : (
          <>
            {isStoreDataEmpty && (
              <StoreLocatorAction
                storeLocatorType={storeLocatorType}
                analyticsEventDetails={bopisEventDetailsTemplate}
              />
            )}

            {storesData && storesData.length === 0 ? (
              <p className="store-locator-no-results">{noResultsCopy}</p>
            ) : storesData ? (
              <div className="store-locator-tile-pdp-container d-flex">
                {pricingDisclaimer && storeLocatorType === 'fis' ? (
                  <p className="store-locator-pricing-disclaimer black-text">{pricingDisclaimer}</p>
                ) : null}
                {storesData.map((storeAvailabilityData: IStoreAvailabilityData) => (
                  <StoreLocatorTile
                    store={storeAvailabilityData}
                    inputName="pdp"
                    page="pdp"
                    showFullTile={false}
                    isAvailableOnline={Boolean(sidebarPdpData?.isAvailableOnline)}
                    key={storeAvailabilityData.location_id}
                    storeSelectionEnabled={storeLocatorType === 'bopis' ? true : false}
                    storeLocatorType={storeLocatorType}
                    shouldShowOmniPdpExperience={shouldShowOmniPdpExperience}
                  />
                ))}
                <div
                  className="details-link"
                  onClick={() => {
                    openStoreLocatorSidebar({
                      feature: storeLocatorType,
                      pdpProps: sidebarPdpData || undefined,
                      page: 'pdp',
                    });
                  }}
                >
                  Check Additional Stores
                </div>
              </div>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
};

StoreLocatorPDP = observer(StoreLocatorPDP);
export { StoreLocatorPDP };
